import { PackageOpen, Truck, Wheat } from "lucide-react";
import { useMemo, useState } from "react";

import type { TransactionList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import {
	TransactionFilters,
	type ClassificationFilter,
	type TransactionFilterView,
} from "@/components/TransactionFilters";
import { TransactionWorkspace } from "@/components/TransactionWorkspace";
import { SupplierPayments } from "@/domain/supplier-payments";
import { FinancePresentation } from "@/presentation/finance";

import { SupplierBarChart } from "@/features/dashboard/SupplierBarChart";

type PaymentCategoryId = "sugar" | "packaging" | "inbound_freight";

const categoryContent: Record<
	PaymentCategoryId,
	{
		view: Exclude<TransactionFilterView, "MOVEMENTS">;
		chartTitle: string;
		groupTitle: string;
		groupDescription: string;
		totalDetail: string;
		paidLabel: string;
		paidDetail: string;
		classifiedDetail: string;
		registeredLabel: string;
		registeredDetail: string;
		groupTone: string;
		icon: React.ReactNode;
	}
> = {
	sugar: {
		view: "SUGAR",
		chartTitle: "Açúcar por fornecedor",
		groupTitle: "Grupo Açúcar",
		groupDescription:
			"Os pagamentos estão classificados por fornecedor. O vínculo com ordens de compra será a próxima camada de conciliação.",
		totalDetail: "de açúcar",
		paidLabel: "Fornecedores pagos",
		paidDetail: "fornecedores de açúcar",
		classifiedDetail: "decisão humana preservada",
		registeredLabel: "Fornecedores cadastrados",
		registeredDetail: "com categoria Açúcar",
		groupTone:
			"border-amber-200 bg-amber-50 text-amber-950 [&_svg]:text-amber-700 [&_.description]:text-amber-800",
		icon: <Wheat className="mt-0.5 size-5 shrink-0" />,
	},
	packaging: {
		view: "PACKAGING",
		chartTitle: "Embalagens por fornecedor",
		groupTitle: "Grupo Embalagens",
		groupDescription:
			"Embalagens são matéria-prima da produção. NF-e, parcelas, boletos e pagamentos permanecem fatos distintos e são ligados automaticamente quando o conjunto fecha sem ambiguidade.",
		totalDetail: "de embalagens",
		paidLabel: "Fornecedores pagos",
		paidDetail: "fornecedores de embalagens",
		classifiedDetail: "regra pelo fornecedor",
		registeredLabel: "Fornecedores cadastrados",
		registeredDetail: "com categoria Embalagens",
		groupTone:
			"border-violet-200 bg-violet-50 text-violet-950 [&_svg]:text-violet-700 [&_.description]:text-violet-800",
		icon: <PackageOpen className="mt-0.5 size-5 shrink-0" />,
	},
	inbound_freight: {
		view: "FREIGHT",
		chartTitle: "Fretes por transportadora",
		groupTitle: "Frete de entrada",
		groupDescription:
			"É o transporte do açúcar das usinas até a SINDI. No banco, cada pagamento continua sendo uma saída; o vínculo com OC, carreta e CT-e virá na conciliação.",
		totalDetail: "de frete de entrada",
		paidLabel: "Transportadoras pagas",
		paidDetail: "açúcar trazido até a SINDI",
		classifiedDetail: "regra confirmada por Juan",
		registeredLabel: "Transportadoras cadastradas",
		registeredDetail: "com categoria Frete de entrada",
		groupTone:
			"border-sky-200 bg-sky-50 text-sky-950 [&_svg]:text-sky-700 [&_.description]:text-sky-800",
		icon: <Truck className="mt-0.5 size-5 shrink-0" />,
	},
};

export function SupplierPaymentsView({
	data,
	categoryId,
}: {
	data: TransactionList;
	categoryId: PaymentCategoryId;
}) {
	const content = categoryContent[categoryId];
	const [classification, setClassification] = useState<ClassificationFilter>("ALL");
	const [supplierId, setSupplierId] = useState("ALL");
	const [search, setSearch] = useState("");
	const suppliers = data.suppliers.filter((supplier) => supplier.category.id === categoryId);
	const categoryTransactions = data.transactions.filter(
		(transaction) =>
			transaction.direction === "DEBIT" && transaction.supplier?.category.id === categoryId,
	);
	const paidSupplierIds = new Set(
		categoryTransactions.flatMap((transaction) =>
			transaction.supplier ? [transaction.supplier.id] : [],
		),
	);
	const paidSuppliers = suppliers.filter((supplier) => paidSupplierIds.has(supplier.id));
	const selectedSupplier = paidSuppliers.find((supplier) => supplier.id === supplierId);
	const scopedTransactions = selectedSupplier
		? categoryTransactions.filter((transaction) => transaction.supplier?.id === selectedSupplier.id)
		: categoryTransactions;
	const filtered = useMemo(() => {
		const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");

		return scopedTransactions.filter((transaction) => {
			if (classification === "PENDING" && transaction.classification) {
				return false;
			}
			if (classification === "CLASSIFIED" && !transaction.classification) {
				return false;
			}
			if (!normalizedSearch) {
				return true;
			}

			return [
				transaction.description,
				transaction.descriptionRaw,
				transaction.receiverName,
				transaction.supplier?.name,
				transaction.supplier?.legalName,
				transaction.classification?.explanation,
				transaction.classification?.destination,
			]
				.filter(Boolean)
				.join(" ")
				.toLocaleLowerCase("pt-BR")
				.includes(normalizedSearch);
		});
	}, [classification, scopedTransactions, search]);
	const totalCents = scopedTransactions.reduce(
		(total, transaction) => total + Math.abs(transaction.amountCents),
		0,
	);
	const classified = scopedTransactions.filter((transaction) => transaction.classification).length;
	const chartItems = SupplierPayments.summarize(data.transactions, categoryId);
	const sugarMills = paidSuppliers.filter((supplier) => supplier.kind === "MILL").length;

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<MetricCard
					label="Total pago no mês"
					value={FinancePresentation.money(totalCents)}
					detail={`${scopedTransactions.length} pagamentos${selectedSupplier ? ` · ${selectedSupplier.name}` : ` ${content.totalDetail}`}`}
				/>
				<MetricCard
					label={content.paidLabel}
					value={String(paidSuppliers.length)}
					detail={
						categoryId === "sugar"
							? `${sugarMills} usinas e ${paidSuppliers.length - sugarMills} outro fornecedor`
							: content.paidDetail
					}
				/>
				<MetricCard
					label="Classificados"
					value={`${classified}/${scopedTransactions.length}`}
					detail={content.classifiedDetail}
					tone="success"
				/>
				<MetricCard
					label={content.registeredLabel}
					value={String(suppliers.length)}
					detail={content.registeredDetail}
				/>
			</section>

			<SupplierBarChart items={chartItems} title={content.chartTitle} />

			<div
				className={`mb-4 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${content.groupTone}`}
			>
				{content.icon}
				<div>
					<p className="font-semibold">{content.groupTitle}</p>
					<p className="description mt-0.5 text-xs leading-5">{content.groupDescription}</p>
				</div>
			</div>

			<TransactionWorkspace
				transactions={filtered}
				filters={
					<TransactionFilters
						view={content.view}
						classification={classification}
						supplierId={supplierId}
						suppliers={paidSuppliers}
						search={search}
						visibleCount={filtered.length}
						onClassification={setClassification}
						onSupplier={setSupplierId}
						onSearch={setSearch}
					/>
				}
			/>
		</>
	);
}
